import { useLayoutEffect, useMemo } from 'react'
import { Route, Switch, Redirect } from 'react-router-dom'
import { AppManifests, getFrameworkVar, LoadingPlaceholder } from '@sparky/framework'
// import { setHubMode } from '../common/utils/Sparky'
import { TopBar, TopBarPlaceholder } from './TopBar'
import HubHome from '../homepage'
import Products from '../products/Products'
import Footer from '../footer/Footer'
import { getRootElement } from '@sparky/framework'
import { HUB_PATH, TSG_NONE, LG_BASE_FONT_SIZE } from '../../constants'
import { getAppsFilterFn } from './hubUtils'
import './hub.scss'
import type { VFC } from 'react'
import type { RouteComponentProps } from 'react-router-dom'
import type { AuthState, Tsg, UserState } from '@sparky/framework'
import type { AppAccessFilterFn } from '../../types'

const Hub: VFC<{ auth: AuthState }> = ({ auth }) => {
  const { instances, apps, user, tsgs, tsg_id, access, loading } = auth
  useLayoutEffect(() => {
    const rootElClasses = getRootElement()?.classList
    if (!rootElClasses) {
      return
    }
    const htmlStyle = document.documentElement.style
    rootElClasses.add('sparky-hub-nav')
    const bodyFontSize = htmlStyle.fontSize
    htmlStyle.fontSize = LG_BASE_FONT_SIZE
    if (!getFrameworkVar('hub')) {
      return () => {
        rootElClasses.remove('sparky-hub-nav')
        htmlStyle.fontSize = bodyFontSize
      }
    }
  }, [])
  const appFilterFn = useMemo(() => getAppsFilterFn(auth.user), [auth.user])
  const appAccessFilterFn: AppAccessFilterFn = useMemo(() => {
    if (!access?.length || tsg_id === TSG_NONE.id) {
      return () => false
    }
    if (!getFrameworkVar('hub_role_filter')) {
      return () => true
    }
    const tsgPrn = access.generatePrn({ tsg_id })
    const tsgAccess = access.get(tsgPrn)
    if (tsgAccess?.roleNames?.length) {
      return () => true
    }
    const appRoleAccessSet = new Set(access.map((res) => res.app_id).filter(Boolean))
    if (apps) { // TODO: temp allow all 3p until new roles for 3p
      for (const app of apps) {
        if (app.is_third_party && (
          !app.rbac_role?.support_rbac || app.rbac_role?.app_role === 'ANY'
        )) {
          appRoleAccessSet.add(app.app_id)
        }
      }
    }
    const filterFn: AppAccessFilterFn = ({ app_id }: { app_id?: string } = {}) => {
      return Boolean(app_id && appRoleAccessSet.has(app_id))
    }
    return filterFn
  }, [access, tsg_id, apps])
  const tsg = useMemo(() => {
    if (!tsg_id || !tsgs) {
      return undefined
    }
    if (tsg_id === TSG_NONE.id) {
      return TSG_NONE
    }
    const tsg = tsgs.get(tsg_id)
    if (tsg) {
      return tsg
    }

    const error = 'not able to find tsg from the tsg tree'
    console.error(error, tsg_id) // eslint-disable-line no-console
    return { id: tsg_id, display_name: tsg_id, error } as Tsg
  }, [tsg_id, tsgs])

  if (!apps?.length || !tsg) {
    return <div className='sparky-hub panwds-tw3' data-testid='sparky-hub'>
      <TopBarPlaceholder />
      <div className='sparky-hub-content-placeholder' data-testid='sparky-hub-content-placeholder'></div>
      <LoadingPlaceholder />
    </div>
  }

  return <div className='sparky-hub panwds-tw3' data-testid='sparky-hub'>
    <TopBar user={user as UserState} tsgs={tsgs} tsg={tsg} apps={apps as AppManifests} />
    <div className='sparky-hub-content' data-testid='sparky-hub-content'>
      <Switch>
        <Route
          exact={true}
          path={HUB_PATH}
        >
          {loading && <LoadingPlaceholder />}
          <HubHome
            instances={instances}
            apps={apps}
            tsg={tsg}
            appFilterFn={appFilterFn}
            appAccessFilterFn={appAccessFilterFn}
          />
        </Route>
        <Route
          exact={true}
          path={'/hub/products'}
        ><Redirect to={HUB_PATH} /></Route>
        <Route
          exact={true}
          path={'/marketplace'}
        ><Redirect to={HUB_PATH} /></Route>
        <Route
          exact={true}
          path={'/marketplace/:app'}
          render={({ match: { params: { app } } }: RouteComponentProps<{ app: string }>) => (
            <Redirect to={`${HUB_PATH}/products/${app}`} />
          )}
        />
        <Route
          exact={true}
          path={'/hub/products/:app'}
          render={({ match: { params: { app } } }: RouteComponentProps<{ app: string }>) => (
            <Products
              instance={instances.get(app)}
              app={apps.get(app) || apps.find((appDef) => appDef.marketplace_content?.url_name === app)}
              appFilterFn={appFilterFn}
              appAccessFilterFn={appAccessFilterFn}
            />
          )}
        />
      </Switch>
    </div>
    <Footer data-testid='sparky-hub-footer' />
  </div>
}

export default Hub
